import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
// import { HolderOutlined } from '@ant-design/icons';
import { Button, Table, Space, message, Modal } from 'antd';
// import { ExclamationCircleOutlined } from '@ant-design/icons';
import { arrayMoveImmutable } from 'array-move';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import './index.less';
import PosterFieldModal from '../PosterFieldModal';
import { getProjectPosterModulesListAllApi, deletePosterFieldApi } from '@services/Selection';

// 拖拽手柄
const DragHandle = SortableHandle(() => (
    <Button type="text" size="small">
        <i className="bi bi-grip-vertical" />
    </Button>
));

// 可拖拽行组件
const SortableItem = SortableElement(({ value, onEdit, onDelete, disabled, getFieldTypeLabel }) => (
    <tr className="ant-table-row">
        <td className="ant-table-cell" style={{ width: 80 }}>
            <span>{getFieldTypeLabel(value.type, 'type')}</span>
        </td>
        <td className="ant-table-cell" style={{ width: 100 }}>
            {value.name}
        </td>
        <td className="ant-table-cell" style={{ width: 200 }}>
            <div style={{ lineHeight: '1.5', color: '#666', whiteSpace: 'wrap', wordBreak: 'break-all' }}>{value.note || '-'}</div>
        </td>
        <td className="ant-table-cell" style={{ width: 80 }}>
            <span style={{ color: value.required === 1 ? '#ff4d4f' : '#999' }}>{value.required === 1 ? '必填' : '非必填'}</span>
        </td>
        <td className="ant-table-cell" style={{ width: 150, position: 'sticky', right: 0, background: '#fff' }}>
            <Space size="small">
                <Button type="link" size="small" onClick={() => onEdit(value)} disabled={disabled}>
                    编辑
                </Button>
                <Button type="link" size="small" danger onClick={() => onDelete(value)} disabled={disabled}>
                    删除
                </Button>
                <DragHandle />
            </Space>
        </td>
    </tr>
));

// 可拖拽列表容器
const SortableList = SortableContainer(({ items, onEdit, onDelete, disabled, getFieldTypeLabel }) => (
    <tbody className="ant-table-tbody">
        {items.map((value, index) => (
            <SortableItem key={`item-${value.id}`} index={index} value={value} onEdit={onEdit} onDelete={onDelete} disabled={disabled} getFieldTypeLabel={getFieldTypeLabel} />
        ))}
    </tbody>
));

// 获取字段类型/形式名称
const getFieldTypeLabel = (value, field) => {
    const typeMap = {
        // builtIn: 字段类型
        0: '自定义',
        1: '团队成员',
        2: '成果摘要',
        // type: 字段形式
        type_1: '图文',
        type_2: '文本',
    };
    if (field === 'type') {
        return typeMap[`type_${value}`] || value;
    }
    return typeMap[value] || value;
};

// 壁报字段管理组件
const PosterFields = ({ projectId, disabled }, ref) => {
    const [openModal, setOpenModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [fieldsList, setFieldsList] = useState([]); // 已选字段列表

    // 暴露方法给父组件
    useImperativeHandle(ref, () => ({
        handleAdd,
        setFieldsList, // 暴露设置字段列表的方法
        fieldsList, // 暴露字段列表供父组件校验
    }));

    // 加载字段列表（使用 listAll 接口）
    const loadFieldsList = useCallback(async () => {
        if (!projectId) return;

        try {
            const res = await getProjectPosterModulesListAllApi({ projectId });
            if (res && res.status_code === 200) {
                // 接口返回结构：{ project, list }
                setFieldsList(res.data?.list || []);
            }
        } catch (error) {
            console.error('加载字段列表失败:', error);
        }
    }, [projectId]);

    // 加载字段列表
    useEffect(() => {
        if (projectId) {
            loadFieldsList();
        }
    }, [projectId, loadFieldsList]);

    // 处理拖拽排序结束
    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex !== newIndex) {
            const newFields = arrayMoveImmutable(fieldsList, oldIndex, newIndex);
            setFieldsList(newFields);
            // 拖拽排序只更新本地数据，不触发保存接口
        }
    };

    // 新增字段
    const handleAdd = () => {
        setEditData(null);
        setOpenModal(true);
    };

    // 编辑字段
    const handleEdit = field => {
        setEditData(field);
        setOpenModal(true);
    };

    // 删除字段
    const handleDelete = field => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle" />,
            content: '确认删除该字段？',
            okText: '确认',
            centered: true,
            cancelText: '取消',
            onOk: async () => {
                try {
                    const res = await deletePosterFieldApi(field.id);
                    if (res && res.status_code === 200) {
                        message.success('删除成功');
                        loadFieldsList();
                    }
                } catch (error) {
                    console.error('删除字段失败:', error);
                }
            },
        });
    };

    // Table 列定义
    const columns = [
        // {
        //     title: '字段类型',
        //     dataIndex: 'builtIn',
        //     key: 'builtIn',
        //     width: 100,
        //     render: (_, record) => (
        //         <span>
        //             {getFieldTypeLabel(record.builtIn)}
        //         </span>
        //     ),
        // },
        {
            title: '字段形式',
            dataIndex: 'type',
            key: 'type',
            width: 80,
            render: (_, record) => <span>{getFieldTypeLabel(record.type, 'type')}</span>,
        },
        {
            title: '显示名称',
            dataIndex: 'name',
            key: 'name',
            width: 100,
        },
        {
            title: '显示说明',
            dataIndex: 'note',
            key: 'note',
            ellipsis: true,
            width: 200,
            render: (_, record) => (
                <div
                    style={{
                        lineHeight: '1.5',
                        color: '#666',
                        whiteSpace: 'wrap',
                        wordBreak: 'break-all',
                    }}
                >
                    {record.note || '-'}
                </div>
            ),
        },
        {
            title: '是否必填',
            dataIndex: 'required',
            key: 'required',
            width: 80,
            render: required => <span style={{ color: required === 1 ? '#ff4d4f' : '#999' }}>{required === 1 ? '必填' : '非必填'}</span>,
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button type="link" size="small" onClick={() => handleEdit(record)} disabled={disabled}>
                        编辑
                    </Button>
                    <Button type="link" size="small" danger onClick={() => handleDelete(record)} disabled={disabled}>
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <>
            <div className="poster-fields-table-wrap">
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={fieldsList}
                    pagination={false}
                    size="medium"
                    className="poster-fields-table"
                    components={{
                        body: {
                            wrapper: ({ ...props }) => <SortableList items={fieldsList} onSortEnd={onSortEnd} onEdit={handleEdit} onDelete={handleDelete} disabled={disabled} getFieldTypeLabel={getFieldTypeLabel} useDragHandle lockAxis="y" helperClass="row-dragging" {...props} />,
                        },
                    }}
                />
            </div>

            <PosterFieldModal open={openModal} onCancel={() => setOpenModal(false)} onSaveSuccess={loadFieldsList} editData={editData} existingFields={fieldsList} projectId={projectId} />
        </>
    );
};

export default forwardRef(PosterFields);
